import { type FC, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import PhotoRelatedSearchLinksContainer from 'dibs-interiors-photos/exports/PhotoRelatedSearchLinksContainer';
import Header from './Header';

import { handleLocaleUrl } from 'dibs-intl/exports/urls';

import styles from './ProjectRelatedSearchesContainer.scss';

import { type ProjectRelatedSearchesContainer_viewer$key } from './__generated__/ProjectRelatedSearchesContainer_viewer.graphql';

const NUM_RELATED_SEARCH_LINKS = 5;

type Props = {
    viewer: ProjectRelatedSearchesContainer_viewer$key;
};

const ProjectRelatedSearchesContainer: FC<Props> = ({ viewer: viewerRef }) => {
    const intl = useIntl();
    const viewer = useFragment(
        graphql`
            fragment ProjectRelatedSearchesContainer_viewer on Viewer {
                photosSearchBrowseQuery(page: 1, first: 1) {
                    facets {
                        imageStyleFacet {
                            label
                            name
                        }
                        imageRoomTypeFacet {
                            label
                            name
                        }
                    }
                }
            }
        `,
        viewerRef
    );

    const relatedPhotoSearchLinks = useMemo(() => {
        const { imageStyleFacet, imageRoomTypeFacet } =
            viewer.photosSearchBrowseQuery?.facets || {};
        const styleFacet = (imageStyleFacet || [])[0];
        const styleLabel = styleFacet?.label;
        const styleName = styleFacet?.name;
        const links = (imageRoomTypeFacet || [])
            .slice(0, NUM_RELATED_SEARCH_LINKS)
            .map(roomFacet => {
                const roomLabel = roomFacet?.label;
                return {
                    path: handleLocaleUrl(
                        `/photos/style/${styleLabel}/room/${roomLabel}/`,
                        intl.locale
                    ),
                    text: `${styleName || ''} ${roomFacet?.name || ''}`,
                };
            });
        return links;
    }, [viewer, intl.locale]);

    return (
        <div className={styles.wrapper}>
            <Header
                title={
                    <FormattedMessage
                        id="abt.interiorsProject.relatedSearches"
                        defaultMessage="Related Searches"
                    />
                }
            />
            <div className={styles.linksContainer}>
                <PhotoRelatedSearchLinksContainer
                    links={relatedPhotoSearchLinks}
                    trackingLabel="project"
                />
            </div>
        </div>
    );
};

export default ProjectRelatedSearchesContainer;
